import { useState } from 'react';
import { Box, Typography } from '../../ui';

/**
 * Drop-zone-styled thumbnail picker for the course editor. Overlays the
 * current image so "current" and "drop new" are the same surface — much
 * more legible than a separate preview block below an Upload button.
 */
export default function ThumbnailPicker({
  previewUrl,
  onFileSelected,
  error,
}: {
  previewUrl: string | null;
  onFileSelected: (file: File) => void;
  error?: string;
}) {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) onFileSelected(file);
  };

  return (
    <Box>
      <Box
        component="label"
        htmlFor="course-thumbnail-input"
        onDragOver={(e: React.DragEvent<HTMLLabelElement>) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          maxWidth: 480,
          aspectRatio: '16 / 9',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: error ? 'error.main' : dragOver ? 'primary.main' : 'divider',
          bgcolor: dragOver ? 'primary.50' : 'grey.50',
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'border-color 150ms ease, background-color 150ms ease',
          '&:hover .replace-overlay': { opacity: 1 },
          '&:hover': {
            borderColor: error ? 'error.main' : 'primary.main',
          },
        }}
      >
        {previewUrl ? (
          <>
            <Box
              component="img"
              src={previewUrl}
              alt="Course thumbnail"
              // `contain` (not `cover`) so admins see the full uploaded image
              // here — even if its aspect doesn't match the 16:9 target
              // frame. Letterboxing in the picker makes off-aspect uploads
              // visually obvious without silently cropping content.
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
            <Box
              className="replace-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                opacity: 0,
                transition: 'opacity 150ms ease',
                fontWeight: 600,
              }}
            >
              Click or drop to replace
            </Box>
          </>
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              textAlign: 'center',
              p: 2,
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Drop an image, or click to upload
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Recommended 16:9, at least 1280×720
            </Typography>
          </Box>
        )}
      </Box>
      <input
        id="course-thumbnail-input"
        type="file"
        hidden
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelected(file);
        }}
      />
      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
